import {
  APPROVAL_PIN_FAILURE_WINDOW_MINUTES,
  APPROVAL_PIN_LOCK_MINUTES,
  APPROVAL_PIN_MAX_FAILURES,
  APPROVAL_PIN_MAX_LENGTH,
  APPROVAL_PIN_MIN_LENGTH,
  PolicyEvidenceErrorCode,
} from '@hapana/shared/schemas/policy-compliance/policy-evidence-schemas'

import type { ApprovalPinStatusData } from '_services/policy-compliance.service'
import { getRequestErrorCode } from '_services/graphql/request-error.utils'

export const DeskUnlockMethod = {
  PIN: 'PIN',
  STAFF_SIGN_IN: 'STAFF_SIGN_IN',
} as const
export type DeskUnlockMethod = (typeof DeskUnlockMethod)[keyof typeof DeskUnlockMethod]

export const DeskPinAvailability = {
  READY: 'READY',
  LOCKED: 'LOCKED',
  NOT_SET: 'NOT_SET',
  UNKNOWN: 'UNKNOWN',
} as const
export type DeskPinAvailability = (typeof DeskPinAvailability)[keyof typeof DeskPinAvailability]

export const DeskPinFailure = {
  INVALID: 'INVALID',
  LOCKED: 'LOCKED',
  NOT_SET: 'NOT_SET',
  GENERIC: 'GENERIC',
} as const
export type DeskPinFailure = (typeof DeskPinFailure)[keyof typeof DeskPinFailure]

export const DESK_PIN_COPY_VALUES = {
  min: APPROVAL_PIN_MIN_LENGTH,
  max: APPROVAL_PIN_MAX_LENGTH,
  failures: APPROVAL_PIN_MAX_FAILURES,
  windowMinutes: APPROVAL_PIN_FAILURE_WINDOW_MINUTES,
  lockMinutes: APPROVAL_PIN_LOCK_MINUTES,
} as const

const parseWireInstant = (value: string): Date =>
  new Date(/^\d+$/.test(value) ? Number(value) : value)

export const resolveDeskPinAvailability = (
  status: ApprovalPinStatusData | undefined,
  now: Date
): DeskPinAvailability => {
  if (!status) {
    return DeskPinAvailability.UNKNOWN
  }
  if (!status.isSet) {
    return DeskPinAvailability.NOT_SET
  }
  const lockedUntil = status.lockedUntil ? parseWireInstant(status.lockedUntil) : null
  return lockedUntil && lockedUntil.getTime() > now.getTime()
    ? DeskPinAvailability.LOCKED
    : DeskPinAvailability.READY
}

export const resolveDeskUnlockMethod = (
  availability: DeskPinAvailability,
  chosen: DeskUnlockMethod | null
): DeskUnlockMethod => {
  if (availability !== DeskPinAvailability.READY) {
    return DeskUnlockMethod.STAFF_SIGN_IN
  }
  return chosen ?? DeskUnlockMethod.PIN
}

const PIN_FAILURE_BY_CODE: Record<string, DeskPinFailure> = {
  [PolicyEvidenceErrorCode.APPROVER_PIN_INVALID]: DeskPinFailure.INVALID,
  [PolicyEvidenceErrorCode.APPROVER_PIN_LOCKED]: DeskPinFailure.LOCKED,
  [PolicyEvidenceErrorCode.APPROVER_PIN_NOT_SET]: DeskPinFailure.NOT_SET,
}

export const resolveDeskPinFailure = (error: unknown): DeskPinFailure => {
  const code = getRequestErrorCode(error)
  return (code && PIN_FAILURE_BY_CODE[code]) || DeskPinFailure.GENERIC
}

export const DESK_PIN_FAILURE_COPY: Record<DeskPinFailure, string> = {
  [DeskPinFailure.INVALID]: 'desk.pinInvalid',
  [DeskPinFailure.LOCKED]: 'desk.pinLocked',
  [DeskPinFailure.NOT_SET]: 'desk.pinNotSet',
  [DeskPinFailure.GENERIC]: 'desk.pinUnavailable',
}
